import { useEffect, useState } from 'react';
import { Link, useSearch } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Badge,
  Button,
  buttonStyles,
  Dialog,
  EmptyState,
  Field,
  Input,
  Select,
  Spinner,
  Textarea,
} from '@zt/ui';
import { ConfirmButton } from '../components/Confirm';
import { Table, td, tdR, th, thR } from '../components/reports/blocks';
import { ColumnChart } from '../components/reports/charts';
import { useDimLabel, useMetricFormat } from '../components/reports/custom';
import { downloadReport } from '../components/reports/download';
import { PrintButton, PrintHeader } from '../components/reports/print';
import { PRESETS, type Preset } from '../components/reports/period';
import { api, ApiError, IdempotencyKey } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { useMoney } from '../lib/money';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import { useStoreChoice } from '../lib/store-choice';
import type { Warehouse } from '../lib/types';
import type { CustomReport, SavedReportList, ShareTargets, WidgetList } from '../lib/types-faz8';

/**
 * Özel rapor oluşturucu (F8-08, ADR-808): veri kümesi, metrik, boyut, süzgeç; pivot tablo, grafik, dosya; kaydetme ve
 * rollere/kişilere paylaşma. İzin listesi sunucudadır (`packages/core/src/reports/builder.ts`); burada yalnızca seçim
 * arayüzü için kopyası vardır. Rehber: docs/faz-8/REPORTS_FINANCE_TR.md#rapor-olusturucu.
 */

type Dataset = 'orders' | 'lines' | 'stock';
const DATASETS: Record<
  Dataset,
  { dims: string[]; metrics: string[]; cost: string[]; dated: boolean; perm: string }
> = {
  orders: {
    dims: [
      'day',
      'week',
      'month',
      'year',
      'store',
      'channel',
      'segment',
      'paymentMethod',
      'country',
      'city',
      'customerType',
    ],
    metrics: [
      'orders',
      'netSales',
      'netSalesExVat',
      'averageOrder',
      'grossSales',
      'deductions',
      'shipping',
      'items',
      'newCustomers',
      'cancelledOrders',
    ],
    cost: [],
    dated: true,
    perm: 'reports.sales.read',
  },
  lines: {
    dims: [
      'day',
      'week',
      'month',
      'year',
      'store',
      'channel',
      'segment',
      'product',
      'variant',
      'category',
      'brand',
    ],
    metrics: ['quantity', 'netSalesExVat', 'orders', 'cogs', 'grossProfit', 'marginBps', 'unknownCostSales'],
    cost: ['cogs', 'grossProfit', 'marginBps', 'unknownCostSales'],
    dated: true,
    perm: 'reports.sales.read',
  },
  stock: {
    dims: ['warehouse', 'product', 'variant', 'category', 'brand', 'expiryMonth'],
    metrics: ['onHand', 'available', 'reserved', 'stockValue', 'unknownCostUnits'],
    cost: ['stockValue'],
    dated: false,
    perm: 'inventory.stock.read',
  },
};
const CHANNELS = ['web', 'b2b', 'manual', 'api'] as const;
const SEGMENTS = ['retail', 'dealer'] as const;
const LIMITS = [25, 50, 100, 250, 500] as const;

interface Def {
  dataset: Dataset;
  metrics: string[];
  rows: string[];
  column: string | null;
  period: { preset: Preset; from?: string; to?: string };
  filters: Partial<
    Record<'storeId' | 'channel' | 'segment' | 'warehouseId' | 'categoryId' | 'brandId', string>
  >;
  sort?: { metric: string; dir: 'asc' | 'desc' };
  limit: number;
  chart: 'none' | 'bar';
}

const initial = (dataset: Dataset = 'orders'): Def => ({
  dataset,
  metrics: [DATASETS[dataset].metrics[0]!, DATASETS[dataset].metrics[1]!],
  rows: [dataset === 'stock' ? 'warehouse' : 'month'],
  column: null,
  period: { preset: 'this_month' },
  filters: {},
  limit: 100,
  chart: 'bar',
});

/** Sunucuya gönderilen tanım (boş süzgeçler atılır; tarihsiz veri kümesinde dönem gönderilmez). */
function payload(d: Def) {
  const filters = Object.fromEntries(Object.entries(d.filters).filter(([, v]) => v));
  return {
    dataset: d.dataset,
    metrics: d.metrics,
    rows: d.rows,
    column: d.column,
    ...(DATASETS[d.dataset].dated ? { period: d.period } : {}),
    filters,
    ...(d.sort ? { sort: d.sort } : {}),
    limit: d.limit,
    chart: d.chart,
  };
}

export function ReportBuilderPage() {
  const t = useT();
  const { can } = usePermissions();
  const [def, setDef] = useState<Def>(() => initial());
  const [runDef, setRunDef] = useState<ReturnType<typeof payload> | null>(null);
  const [current, setCurrent] = useState<SavedReportList[number] | null>(null);
  const [saving, setSaving] = useState<'new' | 'update' | null>(null);
  const run = useQuery({
    queryKey: ['custom-report', runDef],
    queryFn: () => api.post<CustomReport>('/v1/admin/reports/custom/run', runDef),
    enabled: runDef !== null,
    retry: false,
  });
  const saved = useQuery({
    queryKey: ['saved-reports'],
    queryFn: () => api.get<SavedReportList>('/v1/admin/reports/custom'),
  });
  const search = useSearch({ from: '/app/rapor-olusturucu' });
  const widgets = useQuery({
    queryKey: ['dashboard-widgets'],
    queryFn: () => api.get<WidgetList>('/v1/admin/dashboard/widgets'),
  });
  const onDashboard = !!current && !!widgets.data?.some((w) => w.savedReportId === current.id);
  const addWidget = useApiMutation(
    (savedReportId: string) => api.post('/v1/admin/dashboard/widgets', { savedReportId }),
    { invalidate: [['dashboard-widgets']], success: t('admin.reportWidgets.added') },
  );
  const ds = DATASETS[def.dataset];
  const allowedDatasets = (Object.keys(DATASETS) as Dataset[]).filter((k) => can(DATASETS[k].perm));
  const setDataset = (dataset: Dataset) => setDef(initial(dataset));
  const toggleMetric = (m: string) =>
    setDef((d) => {
      const has = d.metrics.includes(m);
      if (has && d.metrics.length === 1) return d;
      const metrics = has
        ? d.metrics.filter((x) => x !== m)
        : d.metrics.length >= 4
          ? d.metrics
          : [...d.metrics, m];
      return { ...d, metrics, sort: d.sort && !metrics.includes(d.sort.metric) ? undefined : d.sort };
    });
  const setRow = (i: number, v: string) =>
    setDef((d) => {
      const rows = [...d.rows];
      if (v) rows[i] = v;
      else rows.splice(i, 1);
      return {
        ...d,
        rows: rows.filter(Boolean),
        column: d.column && rows.includes(d.column) ? null : d.column,
      };
    });
  const open = (r: SavedReportList[number]) => {
    const d = r.definition as unknown as Def;
    setDef({ ...initial(d.dataset), ...d, filters: d.filters ?? {} });
    setCurrent(r);
    setRunDef(payload({ ...initial(d.dataset), ...d, filters: d.filters ?? {} }));
  };
  // Ana panel bileşeninden gelindiyse kayıtlı raporu bir kez aç.
  const [openedFromLink, setOpenedFromLink] = useState(false);
  useEffect(() => {
    if (openedFromLink || !search.rapor || !saved.data) return;
    const r = saved.data.find((x) => x.id === search.rapor);
    if (r) open(r);
    setOpenedFromLink(true);
  }, [openedFromLink, search.rapor, saved.data]);
  const remove = useApiMutation((id: string) => api.del(`/v1/admin/reports/custom/${id}`), {
    invalidate: [['saved-reports']],
    success: t('admin.reportBuilder.deleted'),
    onSuccess: () => setCurrent(null),
  });
  return (
    <PageFrame helpKey="page.report_builder" wide printable>
      <PrintHeader title={current?.name ?? t('admin.reportBuilder.navLabel')} />
      <div className="grid gap-8 lg:grid-cols-[22rem_1fr] print:block">
        <div className="space-y-5 print:hidden">
          <SavedList
            list={saved.data}
            currentId={current?.id ?? null}
            onOpen={open}
            onNew={() => {
              setCurrent(null);
              setDef(initial(allowedDatasets[0] ?? 'orders'));
              setRunDef(null);
            }}
          />
          <Field label={t('admin.reportBuilder.dataset')} help={<HelpTip k="report_builder.metrics" />}>
            {(p) => (
              <Select {...p} value={def.dataset} onChange={(e) => setDataset(e.target.value as Dataset)}>
                {allowedDatasets.map((k) => (
                  <option key={k} value={k}>
                    {t(`admin.reportBuilder.datasets.${k}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <p className="-mt-3 text-xs text-ink-3">{t(`admin.reportBuilder.datasetLeads.${def.dataset}`)}</p>
          <fieldset>
            <legend className="mb-2 text-sm font-medium text-ink">
              {t('admin.reportBuilder.metricsLabel')}
            </legend>
            <div className="grid grid-cols-1 gap-1 sm:grid-cols-2 lg:grid-cols-1">
              {ds.metrics
                .filter((m) => !ds.cost.includes(m) || can('pricing.cost.read'))
                .map((m) => (
                  <label key={m} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={def.metrics.includes(m)}
                      disabled={!def.metrics.includes(m) && def.metrics.length >= 4}
                      onChange={() => toggleMetric(m)}
                    />
                    {t(`admin.reportBuilder.metrics.${m}`)}
                  </label>
                ))}
            </div>
          </fieldset>
          {[0, 1].map((i) =>
            i === 1 && !def.rows[0] ? null : (
              <Field key={i} label={t(i === 0 ? 'admin.reportBuilder.row1' : 'admin.reportBuilder.row2')}>
                {(p) => (
                  <Select {...p} value={def.rows[i] ?? ''} onChange={(e) => setRow(i, e.target.value)}>
                    <option value="">{t('admin.reportBuilder.none')}</option>
                    {ds.dims
                      .filter((x) => !def.rows.includes(x) || def.rows[i] === x)
                      .map((x) => (
                        <option key={x} value={x}>
                          {t(`admin.reportBuilder.dims.${x}`)}
                        </option>
                      ))}
                  </Select>
                )}
              </Field>
            ),
          )}
          <Field label={t('admin.reportBuilder.column')} help={<HelpTip k="report_builder.pivot" />}>
            {(p) => (
              <Select
                {...p}
                value={def.column ?? ''}
                onChange={(e) => setDef((d) => ({ ...d, column: e.target.value || null }))}
              >
                <option value="">{t('admin.reportBuilder.none')}</option>
                {ds.dims
                  .filter((x) => !def.rows.includes(x))
                  .map((x) => (
                    <option key={x} value={x}>
                      {t(`admin.reportBuilder.dims.${x}`)}
                    </option>
                  ))}
              </Select>
            )}
          </Field>
          {def.column && def.metrics.length > 1 && (
            <p role="status" className="-mt-3 text-xs text-amber">
              {t('admin.reportBuilder.pivotOneMetric')}
            </p>
          )}
          <Filters def={def} setDef={setDef} />
          <div className="grid grid-cols-2 gap-3">
            <Field label={t('admin.reportBuilder.sort')}>
              {(p) => (
                <Select
                  {...p}
                  value={def.sort ? `${def.sort.metric}:${def.sort.dir}` : ''}
                  onChange={(e) => {
                    const [metric, dir] = e.target.value.split(':');
                    setDef((d) => ({
                      ...d,
                      sort: metric ? { metric: metric!, dir: dir as 'asc' | 'desc' } : undefined,
                    }));
                  }}
                >
                  <option value="">{t('admin.reportBuilder.sortAuto')}</option>
                  {def.metrics.flatMap((m) => [
                    <option key={`${m}:desc`} value={`${m}:desc`}>
                      {t('admin.reportBuilder.sortDesc', { metric: t(`admin.reportBuilder.metrics.${m}`) })}
                    </option>,
                    <option key={`${m}:asc`} value={`${m}:asc`}>
                      {t('admin.reportBuilder.sortAsc', { metric: t(`admin.reportBuilder.metrics.${m}`) })}
                    </option>,
                  ])}
                </Select>
              )}
            </Field>
            <Field label={t('admin.reportBuilder.limit')}>
              {(p) => (
                <Select
                  {...p}
                  value={def.limit}
                  onChange={(e) => setDef((d) => ({ ...d, limit: Number(e.target.value) }))}
                >
                  {LIMITS.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="primary" onClick={() => setRunDef(payload(def))}>
              {t('admin.reportBuilder.run')}
            </Button>
            {can('reports.custom.manage') && current?.canEdit && (
              <Button onClick={() => setSaving('update')}>{t('admin.reportBuilder.saveChanges')}</Button>
            )}
            {can('reports.custom.manage') && (
              <Button onClick={() => setSaving('new')}>
                {current ? t('admin.reportBuilder.saveAs') : t('admin.reportBuilder.save')}
              </Button>
            )}
            {current &&
              (onDashboard ? (
                <span className="inline-flex items-center text-sm text-ink-3">
                  {t('admin.reportWidgets.onDashboard')}
                </span>
              ) : (
                <Button loading={addWidget.isPending} onClick={() => addWidget.mutate(current.id)}>
                  {t('admin.reportWidgets.addToDashboard')}
                </Button>
              ))}
            {current && can('reports.schedule.manage') && (
              <Link
                to="/rapor-dosyalari"
                search={{ zamanla: current.id }}
                className={buttonStyles({ variant: 'secondary' })}
              >
                {t('admin.reportBuilder.schedule')}
              </Link>
            )}
            {current?.canEdit && (
              <ConfirmButton
                message={t('admin.reportBuilder.deleteConfirm')}
                onConfirm={() => remove.mutateAsync(current.id)}
              >
                {t('admin.reportBuilder.deleteReport')}
              </ConfirmButton>
            )}
          </div>
          {current && !current.canEdit && (
            <p className="text-xs text-ink-3">{t('admin.reportBuilder.readOnly')}</p>
          )}
        </div>

        <div className="min-w-0">
          {current && (
            <p className="mb-2 text-sm font-medium text-ink print:hidden">
              {t('admin.reportBuilder.current', { name: current.name })}
            </p>
          )}
          <Result query={run} name={current?.name ?? null} />
        </div>
      </div>
      {saving && (
        <SaveDialog
          mode={saving}
          current={current}
          definition={payload(def)}
          onClose={() => setSaving(null)}
          onSaved={(r) => setCurrent(r)}
        />
      )}
    </PageFrame>
  );
}

function SavedList({
  list,
  currentId,
  onOpen,
  onNew,
}: {
  list: SavedReportList | undefined;
  currentId: string | null;
  onOpen: (r: SavedReportList[number]) => void;
  onNew: () => void;
}) {
  const t = useT();
  return (
    <section aria-labelledby="saved-reports-title" className="rounded-md border border-line p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h2 id="saved-reports-title" className="text-sm font-semibold text-ink">
          {t('admin.reportBuilder.savedTitle')}
        </h2>
        <Button size="sm" variant="ghost" onClick={onNew}>
          {t('admin.reportBuilder.newReport')}
        </Button>
      </div>
      {!list ? (
        <Spinner label={t('common.loading')} />
      ) : !list.length ? (
        <p className="text-sm text-ink-3">{t('admin.reportBuilder.savedEmpty')}</p>
      ) : (
        <ul className="space-y-1">
          {list.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                aria-current={r.id === currentId ? 'true' : undefined}
                className={`w-full rounded px-2 py-1 text-start text-sm hover:bg-surface-2 ${r.id === currentId ? 'bg-pine-tint font-medium text-pine' : 'text-ink'}`}
                onClick={() => onOpen(r)}
              >
                {r.name}
                <span className="block text-xs text-ink-3">
                  {r.mine
                    ? r.visibility === 'shared'
                      ? `${t('admin.reportBuilder.mine')} · ${t('admin.reportBuilder.sharedMark')}`
                      : t('admin.reportBuilder.mine')
                    : t('admin.reportBuilder.sharedBy', { name: r.ownerName ?? '—' })}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Filters({ def, setDef }: { def: Def; setDef: (f: (d: Def) => Def) => void }) {
  const t = useT();
  const { can } = usePermissions();
  const { stores } = useStoreChoice();
  const ds = DATASETS[def.dataset];
  const categories = useQuery({
    queryKey: ['categories'],
    queryFn: () => api.get<Array<{ id: string; name: string }>>('/v1/admin/categories'),
    enabled: can('catalog.product.read'),
    retry: false,
  });
  const brands = useQuery({
    queryKey: ['brands'],
    queryFn: () => api.get<Array<{ id: string; name: string }>>('/v1/admin/brands'),
    enabled: can('org.brand.read'),
    retry: false,
  });
  const warehouses = useQuery({
    queryKey: ['warehouses'],
    queryFn: () => api.get<Warehouse[]>('/v1/admin/warehouses'),
    enabled: def.dataset === 'stock',
    retry: false,
  });
  const setF = (k: keyof Def['filters'], v: string) =>
    setDef((d) => ({ ...d, filters: { ...d.filters, [k]: v || undefined } }));
  const select = (
    k: keyof Def['filters'],
    label: string,
    options: Array<{ value: string; label: string }>,
  ) => (
    <Field label={label}>
      {(p) => (
        <Select {...p} value={def.filters[k] ?? ''} onChange={(e) => setF(k, e.target.value)}>
          <option value="">{t('admin.reportBuilder.all')}</option>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
      )}
    </Field>
  );
  return (
    <fieldset className="space-y-3">
      <legend className="mb-1 text-sm font-medium text-ink">{t('admin.reportBuilder.filtersTitle')}</legend>
      {ds.dated && (
        <>
          <Field label={t('admin.reports.period')} help={<HelpTip k="reports.period" />}>
            {(p) => (
              <Select
                {...p}
                value={def.period.preset}
                onChange={(e) =>
                  setDef((d) => ({ ...d, period: { ...d.period, preset: e.target.value as Preset } }))
                }
              >
                {PRESETS.map((x) => (
                  <option key={x} value={x}>
                    {t(`admin.reports.presets.${x}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          {def.period.preset === 'custom' && (
            <div className="grid grid-cols-2 gap-3">
              <Field label={t('admin.reports.from')}>
                {(p) => (
                  <Input
                    {...p}
                    type="date"
                    value={def.period.from ?? ''}
                    onChange={(e) => setDef((d) => ({ ...d, period: { ...d.period, from: e.target.value } }))}
                  />
                )}
              </Field>
              <Field label={t('admin.reports.to')}>
                {(p) => (
                  <Input
                    {...p}
                    type="date"
                    value={def.period.to ?? ''}
                    onChange={(e) => setDef((d) => ({ ...d, period: { ...d.period, to: e.target.value } }))}
                  />
                )}
              </Field>
            </div>
          )}
          {stores.length > 1 &&
            select(
              'storeId',
              t('admin.salesReport.store'),
              stores.map((s) => ({ value: s.id, label: s.name })),
            )}
          {select(
            'channel',
            t('admin.salesReport.channel'),
            CHANNELS.map((c) => ({ value: c, label: t(`admin.salesReport.channels.${c}`) })),
          )}
          {select(
            'segment',
            t('admin.salesReport.segment'),
            SEGMENTS.map((c) => ({ value: c, label: t(`admin.salesReport.segments.${c}`) })),
          )}
        </>
      )}
      {def.dataset === 'stock' &&
        warehouses.data &&
        select(
          'warehouseId',
          t('admin.reportBuilder.warehouse'),
          warehouses.data.map((w) => ({ value: w.id, label: w.name })),
        )}
      {categories.data &&
        select(
          'categoryId',
          t('admin.reportBuilder.category'),
          categories.data.map((c) => ({ value: c.id, label: c.name })),
        )}
      {brands.data &&
        select(
          'brandId',
          t('admin.reportBuilder.brand'),
          brands.data.map((b) => ({ value: b.id, label: b.name })),
        )}
    </fieldset>
  );
}

function Result({
  query,
  name,
}: {
  query: { data?: CustomReport; isFetching: boolean; isError: boolean; error: unknown };
  name: string | null;
}) {
  const t = useT();
  const money = useMoney();
  const { formatNumber, formatDate } = useI18n();
  const dimLabel = useDimLabel();
  const fmt = useMetricFormat();
  const [downloading, setDownloading] = useState(false);
  const [idem] = useState(() => new IdempotencyKey());
  const background = useApiMutation(
    (body: { definition: CustomReport['definition']; name: string }) =>
      api.post('/v1/admin/reports/exports', { ...body, format: 'xlsx' }, { idempotencyKey: idem }),
    { invalidate: [['report-exports']], success: t('admin.reportBuilder.backgroundQueued') },
  );
  if (query.isError)
    return (
      <p role="alert" className="text-sm text-rose">
        {query.error instanceof ApiError ? query.error.title : String(query.error)}
      </p>
    );
  if (!query.data) return query.isFetching ? <Spinner label={t('common.loading')} /> : null;
  const r = query.data;
  const metricName = (m: string) => t(`admin.reportBuilder.metrics.${m}`);
  const dimName = (d: string) => t(`admin.reportBuilder.dims.${d}`);
  const caption = t('admin.reportBuilder.resultTitle');
  const first = r.metrics[0]!;
  const chartable = r.definition.chart === 'bar' && !r.column && r.rows.length === 1 && r.data.length > 0;
  const download = async (format: 'csv' | 'xlsx') => {
    setDownloading(true);
    await downloadReport(
      `/v1/admin/reports/custom/export?format=${format}&definition=${encodeURIComponent(JSON.stringify(r.definition))}`,
      `rapor.${format}`,
    );
    setDownloading(false);
  };
  return (
    <section
      aria-label={caption}
      className={query.isFetching ? 'opacity-60 transition-opacity' : 'transition-opacity'}
    >
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-base font-semibold text-ink">
          {name ?? caption}
          {r.period && (
            <span className="ms-2 text-sm font-normal text-ink-3">
              {formatDate(r.period.from, false)} – {formatDate(r.period.to, false)}
            </span>
          )}
        </h2>
        <span className="inline-flex flex-wrap items-center gap-2 print:hidden">
          <Button size="sm" variant="secondary" loading={downloading} onClick={() => void download('csv')}>
            {t('admin.reports.csv')}
          </Button>
          <Button size="sm" variant="secondary" loading={downloading} onClick={() => void download('xlsx')}>
            {t('admin.reports.xlsx')}
          </Button>
          <Button
            size="sm"
            variant="secondary"
            loading={background.isPending}
            onClick={() => background.mutate({ definition: r.definition, name: name ?? caption })}
          >
            {t('admin.reportBuilder.background')}
          </Button>
          <HelpTip k="report_files.background" />
          <PrintButton size="sm" />
        </span>
      </div>
      <div className="mb-3 space-y-1 text-xs text-ink-3">
        {r.hiddenMetrics.length > 0 && (
          <p role="status">
            {t('admin.reportBuilder.hidden', { names: r.hiddenMetrics.map(metricName).join(', ') })}
          </p>
        )}
        {r.multiValued && <p>{t('admin.reportBuilder.multiValued')}</p>}
        {r.fxMissing > 0 && (
          <p className="text-amber">{t('admin.reportBuilder.fxMissing', { count: r.fxMissing })}</p>
        )}
        {r.totalRows > r.data.length && (
          <p>{t('admin.reportBuilder.truncatedRows', { shown: r.data.length, total: r.totalRows })}</p>
        )}
        {r.truncatedColumns && <p>{t('admin.reportBuilder.truncatedColumns')}</p>}
      </div>
      {chartable && first.kind !== 'bps' && (
        <div className="mb-6">
          <ColumnChart
            title={t('admin.reportBuilder.chartTitle', {
              metric: metricName(first.key),
              dim: dimName(r.rows[0]!),
            })}
            points={r.data.slice(0, 36).map((row) => ({
              key: row.keys.join('/'),
              label: dimLabel(r.rows[0]!, row.keys[0]!, row.labels[0] ?? null),
              value: row.values[first.key] ?? 0,
            }))}
            formatValue={(n) => fmt(first.kind, n)}
            formatTick={(n) =>
              first.kind === 'money'
                ? money(Math.round(n / 100) * 100, 'TRY')
                : formatNumber(n, Number.isInteger(n) ? 0 : 1)
            }
          />
        </div>
      )}
      {!r.data.length ? (
        <EmptyState title={t('admin.reportBuilder.empty')} />
      ) : (
        <div className="max-h-[40rem] overflow-auto" tabIndex={0} role="region" aria-label={caption}>
          <Table
            caption={caption}
            head={
              <tr>
                {r.rows.map((d) => (
                  <th key={d} scope="col" className={th}>
                    {dimName(d)}
                  </th>
                ))}
                {r.column
                  ? [
                      ...r.columns.map((c) => (
                        <th key={c.key} scope="col" className={thR}>
                          {dimLabel(r.column!, c.key, c.label)}
                        </th>
                      )),
                      <th key="__total" scope="col" className={thR}>
                        {t('admin.reportBuilder.total')}
                      </th>,
                    ]
                  : r.metrics.map((m) => (
                      <th key={m.key} scope="col" className={thR}>
                        {metricName(m.key)}
                      </th>
                    ))}
              </tr>
            }
          >
            {r.data.map((row) => (
              <tr key={row.keys.join('/')} className="border-t border-line">
                {row.keys.map((k, i) =>
                  i === 0 ? (
                    <th key={i} scope="row" className={`${td} text-start font-normal`}>
                      {dimLabel(r.rows[i]!, k, row.labels[i] ?? null)}
                    </th>
                  ) : (
                    <td key={i} className={td}>
                      {dimLabel(r.rows[i]!, k, row.labels[i] ?? null)}
                    </td>
                  ),
                )}
                {r.rows.length === 0 && (
                  <th scope="row" className={`${td} text-start font-normal`}>
                    {t('admin.reportBuilder.total')}
                  </th>
                )}
                {r.column
                  ? [
                      ...r.columns.map((c) => (
                        <td key={c.key} className={tdR}>
                          {fmt(first.kind, row.cells?.[c.key] ?? null)}
                        </td>
                      )),
                      <td key="__total" className={`${tdR} font-medium`}>
                        {fmt(first.kind, row.values[first.key] ?? null)}
                      </td>,
                    ]
                  : r.metrics.map((m) => (
                      <td key={m.key} className={tdR}>
                        {fmt(m.kind, row.values[m.key] ?? null)}
                      </td>
                    ))}
              </tr>
            ))}
            {r.rows.length > 0 && (
              <tr className="border-t-2 border-line bg-surface-2 font-medium">
                <th scope="row" colSpan={r.rows.length} className={`${td} text-start`}>
                  {t('admin.reportBuilder.total')}
                </th>
                {r.column
                  ? [
                      ...r.columns.map((c) => (
                        <td key={c.key} className={tdR}>
                          {fmt(first.kind, r.totals.cells?.[c.key] ?? null)}
                        </td>
                      )),
                      <td key="__total" className={tdR}>
                        {fmt(first.kind, r.totals.values[first.key] ?? null)}
                      </td>,
                    ]
                  : r.metrics.map((m) => (
                      <td key={m.key} className={tdR}>
                        {fmt(m.kind, r.totals.values[m.key] ?? null)}
                      </td>
                    ))}
              </tr>
            )}
          </Table>
        </div>
      )}
    </section>
  );
}

function SaveDialog({
  mode,
  current,
  definition,
  onClose,
  onSaved,
}: {
  mode: 'new' | 'update';
  current: SavedReportList[number] | null;
  definition: ReturnType<typeof payload>;
  onClose: () => void;
  onSaved: (r: SavedReportList[number]) => void;
}) {
  const t = useT();
  const editing = mode === 'update' && current;
  const [idem] = useState(() => new IdempotencyKey());
  const [name, setName] = useState(editing ? current.name : '');
  const [description, setDescription] = useState(editing ? (current.description ?? '') : '');
  const [visibility, setVisibility] = useState<'private' | 'shared'>(
    editing ? current.visibility : 'private',
  );
  const [roles, setRoles] = useState<string[]>(editing ? current.sharedRoles : []);
  const [users, setUsers] = useState<string[]>(editing ? current.sharedUserIds : []);
  const targets = useQuery({
    queryKey: ['report-share-targets'],
    queryFn: () => api.get<ShareTargets>('/v1/admin/reports/custom/share-targets'),
  });
  const save = useApiMutation(
    () => {
      const body = {
        name: name.trim(),
        description: description.trim() || null,
        definition,
        visibility,
        sharedRoles: visibility === 'shared' ? roles : [],
        sharedUserIds: visibility === 'shared' ? users : [],
      };
      return editing
        ? api.patch<SavedReportList[number]>(`/v1/admin/reports/custom/${current.id}`, body, {
            ifMatch: current.version,
          })
        : api.post<SavedReportList[number]>('/v1/admin/reports/custom', body, { idempotencyKey: idem });
    },
    {
      invalidate: [['saved-reports']],
      success: t('admin.reportBuilder.saved'),
      onSuccess: (r) => {
        onSaved(r);
        onClose();
      },
      silentFieldErrors: true,
    },
  );
  const toggle = (list: string[], set: (v: string[]) => void, v: string) =>
    set(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={t('admin.reportBuilder.saveTitle')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={name.trim().length < 2}
            onClick={() => save.mutate(undefined)}
          >
            {editing ? t('admin.reportBuilder.saveChanges') : t('admin.reportBuilder.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label={t('admin.reportBuilder.name')} required error={fieldError(save.error, 'name')}>
          {(p) => <Input {...p} value={name} onChange={(e) => setName(e.target.value)} />}
        </Field>
        <Field label={t('admin.reportBuilder.description')} optionalLabel={t('common.optional')}>
          {(p) => (
            <Textarea {...p} rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
          )}
        </Field>
        <Field label={t('admin.reportBuilder.visibility')} help={<HelpTip k="report_builder.share" />}>
          {(p) => (
            <Select
              {...p}
              value={visibility}
              onChange={(e) => setVisibility(e.target.value as 'private' | 'shared')}
            >
              <option value="private">{t('admin.reportBuilder.visibilities.private')}</option>
              <option value="shared">{t('admin.reportBuilder.visibilities.shared')}</option>
            </Select>
          )}
        </Field>
        {visibility === 'shared' && targets.data && (
          <>
            <fieldset>
              <legend className="mb-1 text-sm font-medium text-ink">
                {t('admin.reportBuilder.shareRoles')}
              </legend>
              <div className="grid gap-1 sm:grid-cols-2">
                {targets.data.roles
                  .filter((r) => r.key !== 'owner')
                  .map((r) => (
                    <label key={r.key} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={roles.includes(r.key)}
                        onChange={() => toggle(roles, setRoles, r.key)}
                      />
                      {r.system ? t(`roles.${r.key}`) : r.name}
                    </label>
                  ))}
              </div>
            </fieldset>
            <fieldset>
              <legend className="mb-1 text-sm font-medium text-ink">
                {t('admin.reportBuilder.shareUsers')}
              </legend>
              <div className="grid max-h-48 gap-1 overflow-y-auto sm:grid-cols-2">
                {targets.data.members.map((m) => (
                  <label key={m.userId} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={users.includes(m.userId)}
                      onChange={() => toggle(users, setUsers, m.userId)}
                    />
                    {m.name}
                  </label>
                ))}
              </div>
            </fieldset>
            <p className="text-xs text-ink-3">{t('admin.reportBuilder.shareNote')}</p>
          </>
        )}
        {visibility === 'shared' && !targets.data && <Spinner label={t('common.loading')} />}
        <Badge tone="slate">{t(`admin.reportBuilder.datasets.${definition.dataset}`)}</Badge>
      </div>
    </Dialog>
  );
}
